import { forwardRef, useState, useCallback, useEffect } from 'react';
import { ConfigService } from '@nextgen-web-framework/core';
import { type IconName } from '@fortawesome/fontawesome-svg-core';
import { getExampleNumber, parsePhoneNumber, type CountryCode } from 'libphonenumber-js/max';

import examples from 'libphonenumber-js/mobile/examples';
import { Theme, MLPVersion } from '../../types/enums';
import { Data } from '../DropDown/types/propsType';
import { InputTypes, InputValidations } from '../Input/Input';
import { FAIcon } from '../Icons/Icons';
import { useMasterData } from '../MasterData/MasterDataProvider';
import { NextgenImage } from '../NextgenImage/NextgenImage';
import PhoneNumberMLP1 from './mlp1/PhoneNumberMLP1';
import PhoneNumberMLP2 from './mlp2/PhoneNumberMLP2';
import CountrySelector from './CountrySelector/CountrySelector';
import { PhoneNumberProps } from './types/propsType';
import { Country } from './types/countryInterface';
import { InputChangeArgs } from './types/inputChangeHanderInterface';
import styles from './PhoneNumber.module.scss';

const { DEFAULT, INVALID, VALID } = InputValidations;

const compareByName = (a: { name?: string; countryName?: string }, b: { name?: string; countryName?: string }) => {
  const firstName = (a.name || a.countryName) as string;
  const secondName = (b.name || b.countryName) as string;
  return firstName.localeCompare(secondName);
};

const checkMLPVersion = (mlpVersion: MLPVersion, toCompareMLPVersion: MLPVersion) => mlpVersion === toCompareMLPVersion;

const PHONE_NUMBER_LENGTH_OVERRIDES: Partial<Record<CountryCode, number>> = {
  // Workaround: override Phonelib’s incorrect default for Indonesian phone number length by hardcoding the correct length.
  ID: 11,
};

export const getPhoneNumberLengthByCountry = (countryCode: CountryCode): number | undefined => {
  if (typeof countryCode !== 'string') {
    return undefined;
  }
  const cc = countryCode.toUpperCase() as CountryCode;
  // check overrides first
  if (PHONE_NUMBER_LENGTH_OVERRIDES[cc] !== undefined) {
    return PHONE_NUMBER_LENGTH_OVERRIDES[cc];
  }
  // fall back to the example data
  return getExampleNumber(cc, examples)?.nationalNumber?.length;
};

const getSelectedCountry = ({ dropdownValue, data }: { dropdownValue?: string | number | Data; data?: Data[] }) =>
  dropdownValue
    ? data?.filter((item) => {
        if (typeof dropdownValue === 'string') {
          return (
            item.name?.toString().replaceAll(/[()']+/g, '') === dropdownValue?.toString().replaceAll(/[()']+/g, '')
          );
        } else if (typeof dropdownValue === 'object') {
          return (
            item.name?.toString().replaceAll(/[()']+/g, '') ===
            dropdownValue?.name?.toString().replaceAll(/[()']+/g, '')
          );
        }
      })?.[0]
    : data?.[0];

const getAllowedLength = (selectedCountryData: any, autoLeadingZero: boolean): number => {
  const baseLength = Number(selectedCountryData?.phoneNumberLength) || 0;
  return autoLeadingZero ? baseLength + 1 : baseLength;
};

const extractNumeric = (value: string | undefined): string => value?.match(/\d+/g)?.join('') || '';

const VALID_PHONE_CHARS_REGEX = /[^0-9+\-\s()]/g;

export const sanitizePhone = (raw?: string): string => (raw ?? '').replace(VALID_PHONE_CHARS_REGEX, '');

export const applyAutoLeadingZero = (
  resolvedValue: string | undefined,
  autoLeadingZero: boolean,
): string | undefined => {
  if (resolvedValue === undefined) {
    return resolvedValue;
  }
  if (resolvedValue === '') {
    return '';
  } // Do not prepend zero if the resolved value is empty // CHANGED
  return autoLeadingZero && !resolvedValue.startsWith('0') ? `0${resolvedValue}` : resolvedValue;
};

// Determine the resolved value based on input type and allowed length
export const getResolvedValue = (
  value: string,
  allowedLength: number,
  isCountrySelector: boolean,
  isMLP1: boolean,
  isTextInput: boolean,
  numericLength: number,
): string | undefined => {
  if (isCountrySelector || isMLP1) {
    return value.substring(0, allowedLength);
  }
  if (isTextInput) {
    return numericLength <= allowedLength ? value : undefined;
  }
  return allowedLength ? value.substring(0, allowedLength) : value;
};

const handleOnInputChange = ({
  isCountrySelector,
  mlpVersion,
  restProps,
  validatePhoneNumber,
  setCurrentStatus,
  value: rawValue,
  data,
  onInputChange,
  autoLeadingZero,
  isAutoValidate,
}: InputChangeArgs & { isAutoValidate: boolean }) => {
  const value = sanitizePhone(rawValue);
  const { dropdownValue } = restProps;
  const numericPhoneNumber = extractNumeric(value);

  const returnWithDefault = (val: string) => {
    onInputChange?.(val, DEFAULT);
    setCurrentStatus(DEFAULT);
  };

  const getNumericWithLeadingZero = (): string => {
    if (numericPhoneNumber === '') {
      return '';
    }
    if (autoLeadingZero && !numericPhoneNumber.startsWith('0')) {
      return `0${numericPhoneNumber}`;
    }
    return numericPhoneNumber;
  };

  if (!dropdownValue || !data) {
    returnWithDefault(getNumericWithLeadingZero());
    return;
  }

  const selectedCountryData = getSelectedCountry({ dropdownValue, data });
  const allowedLength = getAllowedLength(selectedCountryData, autoLeadingZero);

  if (numericPhoneNumber === '') {
    if (isAutoValidate) {
      const currentStatus = validatePhoneNumber(dropdownValue as Data, '');
      onInputChange?.('', currentStatus);
      setCurrentStatus(currentStatus);
    } else {
      returnWithDefault('');
    }
    return;
  }

  const numericWithLeadingZero = getNumericWithLeadingZero();
  const trimmedNumeric = numericWithLeadingZero.slice(0, allowedLength);
  const isMLP1 = checkMLPVersion(mlpVersion, MLPVersion.ONE);
  const isTextInput = isNaN(Number(value));

  let valueResolved = getResolvedValue(
    value || '',
    allowedLength,
    isCountrySelector,
    isMLP1,
    isTextInput,
    numericPhoneNumber.length,
  );

  valueResolved = applyAutoLeadingZero(valueResolved, autoLeadingZero);

  if (valueResolved === undefined) {
    return;
  }

  if (isAutoValidate) {
    const currentStatus = validatePhoneNumber(dropdownValue as Data, trimmedNumeric);
    onInputChange?.(valueResolved, currentStatus);
    setCurrentStatus(currentStatus);
  } else {
    onInputChange?.(valueResolved, DEFAULT);
  }
};

const getDropdownData = (data: Data[] | undefined, countryData: Country[], mlpVersion: MLPVersion) => {
  if (data) {
    data.forEach((item) => {
      item.phoneNumberLength = getPhoneNumberLengthByCountry(item.id as CountryCode);
    });
    const sortedCountriesData = data.sort(compareByName);
    return sortedCountriesData;
  }

  if (countryData) {
    const sortedCountryData = countryData.sort((a, b) => a.countryName.localeCompare(b.countryName)); // Sorting the array alphabetically by countryName
    const configs = ConfigService.getConfigs();
    if (checkMLPVersion(mlpVersion, MLPVersion.ONE)) {
      return sortedCountryData.map((item) => ({
        id: item.pk,
        name: item.countryName,
        desc: `+${item.phoneCode}`,
        phoneNumberLength: getPhoneNumberLengthByCountry(item.pk as CountryCode),
        flagIcon: (
          <div>
            <NextgenImage src={`${configs.NEXT_ASSETS_BASE_PATH}/icons/flags/${item.icon}`} alt="flag icon" />
          </div>
        ),
      }));
    }

    return sortedCountryData.map((item) => ({
      id: item.pk,
      name: `(+${item.phoneCode})`,
      desc: item.countryName,
      phoneNumberLength: getPhoneNumberLengthByCountry(item.pk as CountryCode),
    }));
  }
  return [];
};

const inputValidationStatus = (status?: InputValidations) => {
  if (status) {
    if (status === VALID) {
      return styles['valid'];
    } else if (status === INVALID) {
      return styles['invalid'];
    }
  }

  return '';
};

const disabledStateClass = (dropdownDisabled: boolean, inputDisabled: boolean) => {
  if (dropdownDisabled && inputDisabled) {
    return styles['disabled'];
  } else if (dropdownDisabled) {
    return styles['disabled-dropdown'];
  } else if (inputDisabled) {
    return styles['disabled-input'];
  }
  return '';
};

const calculateCurrentIcon = function (status?: InputValidations) {
  if (status) {
    if (status === VALID) {
      return { iconName: 'circle-check' as IconName };
    } else if (status === INVALID) {
      return { iconName: 'square-exclamation' as IconName };
    }
  }

  return null;
};

const getChild = (
  isCountrySelector: boolean,
  phoneCountryData: Data[],
  dropdownDisabled: boolean,
  mlpVersion: MLPVersion,
  restProps: any,
  commonMLPProps: any,
) => {
  let child = <></>;
  if (isCountrySelector) {
    child = (
      <CountrySelector data={phoneCountryData} dropdownDisabled={dropdownDisabled} status={status} {...restProps} />
    );
  } else if (checkMLPVersion(mlpVersion, MLPVersion.ONE)) {
    child = <PhoneNumberMLP1 {...commonMLPProps} />;
  } else if (checkMLPVersion(mlpVersion, MLPVersion.TWO)) {
    child = <PhoneNumberMLP2 {...commonMLPProps} />;
  }
  return child;
};

export const phoneNumberValidation =
  (
    restProps: PhoneNumberProps,
    phoneCountryData: Data[],
    isPhoneNumberValid: (isValid: boolean) => void,
    autoLeadingZero: boolean,
  ) =>
  (selectedCountry: Data | string, inputNumber: string): InputValidations => {
    const configLabel = restProps.configData?.label ?? 'name';
    const requiredFallback = restProps.required ? INVALID : DEFAULT;

    const resolveCountry = (): Data | string | undefined => {
      if (typeof selectedCountry !== 'string') {
        return selectedCountry;
      }

      const match = phoneCountryData.find((pcd) => pcd[configLabel] === selectedCountry);
      return match || selectedCountry;
    };

    const currentCountry = resolveCountry();
    const isObjectCountry = currentCountry && typeof currentCountry === 'object';

    if (!isObjectCountry) {
      isPhoneNumberValid(false);
      return requiredFallback;
    }

    const numericInput = inputNumber.replace(/\D+/g, '');

    if (numericInput.length === 0) {
      isPhoneNumberValid(false);
      return requiredFallback;
    }

    const baseLength = Number(currentCountry.phoneNumberLength) || 0;
    const allowedLength = autoLeadingZero ? baseLength + 1 : baseLength;

    if (numericInput.length !== allowedLength) {
      isPhoneNumberValid(false);
      return INVALID;
    }
    const cleanedInputNumber = inputNumber?.startsWith('0') ? inputNumber.slice(1) : inputNumber;
    try {
      const parsed = parsePhoneNumber(cleanedInputNumber, (currentCountry.id as string).toUpperCase() as CountryCode);
      const isValid = parsed.isValid();

      isPhoneNumberValid(isValid);
      return isValid ? VALID : INVALID;
    } catch {
      isPhoneNumberValid(false);
      return requiredFallback;
    }
  };

/**
 *
 *  by default : disabledFilter, dropdownDisabled, inputDisabled is false;
 *  name is required prop
 */

const PhoneNumber = forwardRef<HTMLDivElement, PhoneNumberProps>(
  (
    {
      className,
      dropdownDisabled = false,
      data,
      inputLabel = '',
      inputDisabled = false,
      message,
      status,
      forwardedRef,
      isCountrySelector = false,
      mlpVersion = isCountrySelector ? MLPVersion.ONE : MLPVersion.TWO,
      filterDropdownValues,
      onInputChange,
      isAutoValidate = false,
      displayFlagIcon = false,
      autoLeadingZero = false,
      isPhoneNumberValid = () => null,
      inputWithDropdown = false,
      ...restProps
    }: PhoneNumberProps,
    ref,
  ): JSX.Element => {
    const [currentStatus, setCurrentStatus] = useState<InputValidations | undefined>(status);
    const countryData = useMasterData('MasterCountryList') as Country[];
    const resolvedCountryData = getDropdownData(data, countryData, mlpVersion);
    const phoneCountryData = filterDropdownValues ? filterDropdownValues(resolvedCountryData) : resolvedCountryData;
    const [isElementFocused, setIsElementFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const currentIcon = calculateCurrentIcon(currentStatus);
    const [searchFilterValue, setSearchFilterValue] = useState('');
    const [isTouched, setIsTouched] = useState(false);

    const { dropdownValue, inputValue } = restProps;

    const validatePhoneNumberFn = phoneNumberValidation(
      restProps,
      phoneCountryData,
      isPhoneNumberValid,
      autoLeadingZero,
    );
    const validatePhoneNumber = useCallback(
      (selectedCountry: Data, inputNumber: string) => validatePhoneNumberFn(selectedCountry, inputNumber),
      [restProps.dropdownValue, restProps.inputValue],
    );

    const shouldValidateInput = (
      isAutoValidate: boolean,
      dropdownValue: unknown,
      updatedValue: string,
      isTouched: boolean,
    ): boolean => isAutoValidate && !!dropdownValue && (updatedValue !== '' || isTouched);

    const handleValidatedInput = (
      dropdownValue: Data,
      updatedValue: string,
      setStatus: (status: InputValidations) => void,
      onChange?: (value: string, status: InputValidations) => void,
    ): void => {
      const newStatus = validatePhoneNumber(dropdownValue, updatedValue || '');
      setStatus(newStatus as InputValidations);
      onChange?.(updatedValue, newStatus);
    };

    // On mount or input change:
    // - If inputValue exists, apply autoLeadingZero and validate immediately
    // - If inputValue is empty, validate only after user has interacted
    // - Ensures required validation does not trigger before user action
    useEffect(() => {
      const updatedValue = applyAutoLeadingZero(inputValue, autoLeadingZero) ?? '';

      if (shouldValidateInput(isAutoValidate, dropdownValue, updatedValue, isTouched)) {
        handleValidatedInput(dropdownValue as Data, updatedValue, setCurrentStatus, onInputChange);
      } else if (isAutoValidate) {
        setCurrentStatus(DEFAULT);
      } else if (status) {
        setCurrentStatus(status);
      }
    }, [dropdownValue, inputValue, status, isAutoValidate, isCountrySelector, isTouched]);

    const commonMLPProps = {
      phoneCountryData,
      isElementFocused,
      setIsElementFocused,
      isHovered,
      inputLabel,
      setIsHovered,
      currentIcon,
      dropdownDisabled,
      inputDisabled,
      onInputChange: (value: string | undefined) => {
        if (!isTouched) {
          setIsTouched(true);
        }
        handleOnInputChange({
          isCountrySelector,
          mlpVersion,
          restProps,
          validatePhoneNumber,
          setCurrentStatus,
          value,
          data: resolvedCountryData,
          onInputChange,
          autoLeadingZero,
          isAutoValidate,
        });
      },
      status: currentStatus,
      searchFilterValue: searchFilterValue,
      setSearchFilterValue: setSearchFilterValue,
      displayFlagIcon: displayFlagIcon,
      ...restProps,
      inputValue: applyAutoLeadingZero(inputValue, autoLeadingZero),
      inputWithDropdown: inputWithDropdown,
    };

    return (
      <div
        className={`${styles['phone-number-wrapper']} ${disabledStateClass(
          dropdownDisabled,
          inputDisabled,
        )} ${inputValidationStatus(currentStatus)} ${className} ${styles[`mlp-${mlpVersion}`]}`}
        data-testid="phone-container"
        ref={ref ?? forwardedRef}
      >
        {getChild(isCountrySelector, phoneCountryData, dropdownDisabled, mlpVersion, restProps, commonMLPProps)}
        {message ? (
          <span
            role="presentation"
            aria-label="message"
            aria-details={`${inputLabel} phone number message`}
            className={`${styles['phone-number-message-container']}`}
          >
            {currentIcon ? (
              <FAIcon {...currentIcon} theme={Theme.BLACK} className={styles['phone-number-message-icon']} />
            ) : (
              ''
            )}
            {message && (
              <span data-testid="phone-number-message" className={styles['phone-number-message']}>
                {message}
              </span>
            )}
          </span>
        ) : (
          ''
        )}
      </div>
    );
  },
);
PhoneNumber.displayName = 'PhoneNumber';
export { InputTypes, MLPVersion };
export default PhoneNumber;
